import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import { useCaseClaims, type Claim, type ClaimElement, type ClaimArrow } from "../../hooks/useCaseClaims";
import { useMusterArchers } from "../../hooks/useMusterArchers";
import { useLex } from "../../lib/theme/ThemeProvider";
import InferenceChat from "../../components/InferenceChat";

/**
 * Light the Arrows — the Archers.
 *
 * For each cause of action in the operative complaint, the archers fire the
 * available defenses (Shield Wall authorities + corpus evidence) at it and
 * assess, ELEMENT BY ELEMENT, what is still intact after the volley.
 *   intact    → element still stands for the plaintiff (a live threat)
 *   contested → a real but non-dispositive rebuttal exists
 *   defeated  → a strong defense negates the element (arrow landed)
 * A claim falls if any required element is defeated.
 */
function LightTheArrows() {
  const { caseId } = useParams<{ caseId: string }>();
  const lex = useLex();
  const { data: claims, isLoading, error } = useCaseClaims(caseId);
  const { muster, mustering, error: mErr, status } = useMusterArchers(caseId);
  const changed = status?.authorities_changed_since ?? 0;
  const [open, setOpen] = useState<string | null>(null);

  const tally = countSurvival(claims ?? []);

  return (
    <CommandLayout caseId={caseId} variant="ctafinal" currentCommand="arrows">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">{`// ${lex.commands.arrows.verb.toLowerCase()}`}</p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
          Loose every defense at the claim. See what's still standing.
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          Each cause of action is decomposed into the elements the plaintiff must prove. The archers
          fire authorities and evidence at each element; what survives the volley is the live threat.
          Every arrow cites its source.
        </p>
        <p className="mt-2">
          <Link to={`/case/${caseId}/relevance`} className="font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright">
            → review relevance (Layer 2): which facts carry each element
          </Link>
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          {status?.last_mustered && (
            <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
              {lex.commands.arrows.lastLabel} {new Date(status.last_mustered).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {changed > 0 ? ` · ${changed} authority change${changed === 1 ? "" : "s"} since` : ""}
            </span>
          )}
          <button
            type="button"
            disabled={!caseId || mustering}
            onClick={() => muster()}
            className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
              mustering
                ? "border-ember-bright/40 bg-ember/10 text-ember-bright"
                : changed > 0
                  ? "border-ember-bright/70 bg-ember/20 text-ember-bright shadow-ember-soft animate-pulse-ember"
                  : "border-ember-bright/50 bg-ember/10 text-bone hover:bg-ember/30 hover:text-ember-bright"
            }`}
          >
            {mustering ? lex.commands.arrows.pending : `⤧ ${lex.commands.arrows.action}`}
          </button>
        </div>
        {mErr && <p className="mt-2 font-display text-base text-ember-bright">// muster failed: {mErr}</p>}
        {mustering && (
          <p className="mt-2 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
            // decomposing each claim · firing authorities + evidence at every element…
          </p>
        )}

        {claims && claims.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 font-display text-base font-light uppercase tracking-[0.1em]">
            <Tally label="standing" n={tally.intact} color={sc("intact")} />
            <Tally label="contested" n={tally.contested} color={sc("contested")} />
            <Tally label="defeated" n={tally.defeated} color={sc("defeated")} />
          </div>
        )}
      </header>

      <div className="space-y-3">
        {isLoading && <p className="font-display text-base text-bone-mute">{`// ${lex.ui.arrowsNocking}`}</p>}
        {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
        {!isLoading && !error && (claims?.length ?? 0) === 0 && (
          <p className="rounded border border-steel-dark bg-iron/70 px-5 py-6 font-display text-base text-bone-mute">
            // no claims assessed yet — muster the archers to fire the first volley
          </p>
        )}
        {(claims ?? []).map((c) => (
          <ClaimCard key={c.id} caseId={caseId} claim={c} open={open === c.id} onToggle={() => setOpen(open === c.id ? null : c.id)} />
        ))}
      </div>
    </CommandLayout>
  );
}

/* ============================ palette ============================ */

const SC: Record<string, string> = {
  intact: "#b3503f",     // claim element still stands — a live threat
  contested: "#c9a23a",  // rebutted but not dispositive
  defeated: "#5a8a5a",   // arrow landed — element neutralized
  unassessed: "#5a6470",
};
const sc = (k: string): string => SC[k] ?? SC.unassessed!;
const survivalWord: Record<string, string> = {
  intact: "stands", contested: "contested", defeated: "broken", unassessed: "unassessed",
};
const ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

function countSurvival(claims: Claim[]) {
  const t = { intact: 0, contested: 0, defeated: 0 };
  for (const c of claims) {
    if (c.survival === "intact") t.intact++;
    else if (c.survival === "contested") t.contested++;
    else if (c.survival === "defeated") t.defeated++;
  }
  return t;
}

function Tally({ label, n, color }: { label: string; n: number; color: string }) {
  return (
    <span className="flex items-center gap-1.5 text-bone-mute">
      <i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
      {n} {label}
    </span>
  );
}

/* ============================ claim card ============================ */

function claimContext(c: Claim): string {
  const els = c.elements
    .map((e) => `• ${e.name}: ${survivalWord[e.status] ?? e.status}${e.rationale ? ` — ${e.rationale}` : ""}`)
    .join("\n");
  return [
    `Cause of action: ${c.claim_name} (overall: ${survivalWord[c.survival] ?? c.survival}).`,
    c.survival_rationale ? `Why: ${c.survival_rationale}` : "",
    c.target ? `Asserted against: ${c.target}` : "",
    els ? `Elements assessed:\n${els}` : "",
  ].filter(Boolean).join("\n");
}

function ClaimCard({ caseId, claim, open, onToggle }: { caseId?: string; claim: Claim; open: boolean; onToggle: () => void }) {
  const color = sc(claim.survival);
  const [chat, setChat] = useState(false);
  return (
    <div className="overflow-hidden rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <button onClick={onToggle} className="flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-iron-2/40">
        <span className="mt-0.5 font-display text-lg font-light text-bone-mute" style={{ minWidth: 28 }}>
          {ROMAN[claim.ordinal ?? 0] || claim.ordinal}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-base font-light text-bone">{claim.claim_name}</h3>
            <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em]" style={{ background: color, color: "#0c0f12" }}>
              {survivalWord[claim.survival] ?? claim.survival}
            </span>
          </div>
          {/* armor strip — one segment per element, colored by what survives */}
          <div className="mt-2 flex gap-1">
            {claim.elements.map((e) => (
              <span key={e.id} title={`${e.name}: ${e.status}`} className="h-2 flex-1 rounded-sm"
                style={{ background: sc(e.status), boxShadow: e.status === "intact" ? `0 0 6px ${sc(e.status)}` : undefined }} />
            ))}
          </div>
          {claim.survival_rationale && (
            <p className="mt-2 font-display text-[12px] leading-snug text-bone-dim">{claim.survival_rationale}</p>
          )}
          {claim.target && (
            <p className="mt-1 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/70">vs {claim.target}</p>
          )}
        </div>
        <span className="shrink-0 font-display text-base text-bone-mute">{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div className="border-t border-steel-dark/70 bg-black/20 px-5 py-4">
          <div className="space-y-3">
            {claim.elements.map((e) => <ElementRow key={e.id} el={e} />)}
            {claim.elements.length === 0 && <p className="font-display text-base text-bone-mute">// no elements assessed</p>}
          </div>
          <div className="mt-3">
            <button type="button" onClick={() => setChat((v) => !v)}
              className="font-display text-sm text-blue-bright/80 transition-colors hover:text-blue-bright">
              ⌕ interrogate this assessment {chat ? "▾" : "▸"}
            </button>
            {chat && (
              <InferenceChat caseId={caseId} surface="archers_claim" refId={claim.id}
                label={claim.claim_name} context={claimContext(claim)} onClose={() => setChat(false)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ElementRow({ el }: { el: ClaimElement }) {
  const color = sc(el.status);
  return (
    <div className="rounded border border-steel-dark/70 bg-iron/50 px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: color }} />
        <span className="font-display text-[13px] font-medium text-bone">{el.name}</span>
        <span className="font-display text-sm font-light uppercase tracking-[0.1em]" style={{ color }}>{el.status}</span>
      </div>
      {el.must_prove && <p className="mt-1 font-display text-base leading-snug text-bone-mute">must prove: {el.must_prove}</p>}
      {el.rationale && <p className="mt-1 font-display text-[12px] leading-snug text-bone-dim">{el.rationale}</p>}
      {el.arrows.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {el.arrows.map((ar) => <ArrowRow key={ar.id} ar={ar} />)}
        </ul>
      )}
    </div>
  );
}

function ArrowRow({ ar }: { ar: ClaimArrow }) {
  const glyph = ar.arrow_kind === "authority" ? "⛨" : ar.arrow_kind === "contradiction" ? "⚖" : "✦";
  const strengthColor = ar.strength === "strong" ? "#e8703a" : ar.strength === "weak" ? "#6b7480" : "#c9a23a";
  return (
    <li className="flex items-start gap-2 rounded border border-steel-dark/50 bg-black/20 px-2.5 py-1.5">
      <span className="mt-0.5 shrink-0 text-[12px]" style={{ color: strengthColor }}>{glyph}</span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-[12px] leading-snug text-bone-dim">{ar.thrust}</p>
        <p className="mt-0.5 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute/70">
          {ar.arrow_kind} · {ar.strength}{ar.arrow_kind === "contradiction" && ar.contra_paragraph ? ` · ¶${ar.contra_paragraph}` : ""}{ar.source_label ? ` · ${ar.source_label}` : ""}
        </p>
      </div>
    </li>
  );
}

export default LightTheArrows;
